import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { CATEGORIES } from "../../lib/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
    name: "",
    company: "",
    country: "",
    email: "",
    phone: "",
    category: "",
    products: "",
    message: "",
};

export default function RFQForm() {
    const [form, setForm] = useState(INITIAL);
    const [status, setStatus] = useState({ loading: false, ok: false, err: "" });

    const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, ok: false, err: "" });
        try {
            await axios.post(`${API}/inquiries`, form);
            setStatus({ loading: false, ok: true, err: "" });
            setForm(INITIAL);
        } catch (err) {
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Unable to submit right now. Please try again.";
            setStatus({
                loading: false,
                ok: false,
                err: typeof msg === "string" ? msg : "Submission failed.",
            });
        }
    };

    return (
        <section
            id="inquiry"
            className="py-24 md:py-32"
            data-testid="rfq-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
                <div className="md:col-span-5">
                    <div className="overline mb-4">05 / Request a Quote</div>
                    <h2 className="font-cabinet font-bold text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
                        Tell us what you need.
                        <br />
                        <span className="italic font-normal text-stone-500">
                            We'll respond within 24 hours.
                        </span>
                    </h2>
                    <p className="mt-6 text-lg text-stone-700 leading-relaxed">
                        Share your product list, volume, target country and
                        documentation requirements (CoA, CoO, FSC, CE, etc.)
                        — our export desk will send a structured quotation with
                        pricing, lead time and Incoterms.
                    </p>
                    <div className="mt-10 space-y-4 font-mono text-xs uppercase tracking-[0.18em] text-stone-500">
                        <div className="flex items-center gap-3">
                            <span className="w-6 h-px bg-stone-400" />
                            Pharma / AYUSH / Cosmetics
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-6 h-px bg-stone-400" />
                            Hospital Equipment & Furniture
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-6 h-px bg-stone-400" />
                            Turnkey Hospital Projects
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="w-6 h-px bg-stone-400" />
                            Vaccines & Family Planning
                        </div>
                    </div>
                </div>

                <div className="md:col-span-7">
                    <form
                        onSubmit={submit}
                        className="bg-white border border-stone-200 p-6 md:p-10"
                        data-testid="rfq-form"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="field-label">
                                    Full Name *
                                </label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={handle("name")}
                                    className="field-input"
                                    placeholder="Jane Doe"
                                    data-testid="rfq-name"
                                />
                            </div>
                            <div>
                                <label className="field-label">Company</label>
                                <input
                                    value={form.company}
                                    onChange={handle("company")}
                                    className="field-input"
                                    placeholder="Your organisation"
                                    data-testid="rfq-company"
                                />
                            </div>
                            <div>
                                <label className="field-label">
                                    Country *
                                </label>
                                <input
                                    required
                                    value={form.country}
                                    onChange={handle("country")}
                                    className="field-input"
                                    placeholder="e.g. Nigeria"
                                    data-testid="rfq-country"
                                />
                            </div>
                            <div>
                                <label className="field-label">Email *</label>
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={handle("email")}
                                    className="field-input"
                                    placeholder="you@company.com"
                                    data-testid="rfq-email"
                                />
                            </div>
                            <div>
                                <label className="field-label">
                                    Phone / WhatsApp
                                </label>
                                <input
                                    value={form.phone}
                                    onChange={handle("phone")}
                                    className="field-input"
                                    placeholder="+1 555 000 0000"
                                    data-testid="rfq-phone"
                                />
                            </div>
                            <div>
                                <label className="field-label">
                                    Primary Category
                                </label>
                                <select
                                    value={form.category}
                                    onChange={handle("category")}
                                    className="field-input bg-white"
                                    data-testid="rfq-category"
                                >
                                    <option value="">Select category</option>
                                    {CATEGORIES.map((c) => (
                                        <option key={c.slug} value={c.name}>
                                            {c.name}
                                        </option>
                                    ))}
                                    <option value="Hospital Planning Services">
                                        Hospital Planning Services
                                    </option>
                                    <option value="Momia PharmaOps (SaaS)">
                                        Momia PharmaOps (SaaS — Inventory · SPC · Billing)
                                    </option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className="field-label">
                                Products / SKUs (optional)
                            </label>
                            <textarea
                                value={form.products}
                                onChange={handle("products")}
                                className="field-input min-h-[80px]"
                                placeholder="e.g. Pantoprazole 40mg × 50k strips, Metformin 500mg × 100k tabs"
                                data-testid="rfq-products"
                            />
                        </div>

                        <div className="mt-5">
                            <label className="field-label">
                                Requirement Details *
                            </label>
                            <textarea
                                required
                                value={form.message}
                                onChange={handle("message")}
                                className="field-input min-h-[120px]"
                                placeholder="Volumes, target country, documentation needs (CoA, CoO, registration), timeline, Incoterms..."
                                data-testid="rfq-message"
                            />
                        </div>

                        {status.err && (
                            <div
                                className="mt-5 p-4 border border-red-300 bg-red-50 text-red-700 text-sm"
                                data-testid="rfq-error"
                            >
                                {status.err}
                            </div>
                        )}

                        {status.ok && (
                            <div
                                className="mt-5 p-4 border border-[#4D7C0F] bg-[#f2f8e9] text-[#3a5f08] text-sm flex items-start gap-3"
                                data-testid="rfq-success"
                            >
                                <CheckCircle2
                                    size={18}
                                    strokeWidth={1.8}
                                    className="mt-0.5"
                                />
                                <div>
                                    Thank you — your inquiry has been received.
                                    Our export desk will respond within 24
                                    hours.
                                </div>
                            </div>
                        )}

                        <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-stone-500">
                                * Required fields
                            </p>
                            <button
                                type="submit"
                                disabled={status.loading}
                                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="rfq-submit"
                            >
                                {status.loading ? (
                                    <>
                                        <Loader2
                                            size={18}
                                            className="animate-spin"
                                            strokeWidth={1.5}
                                        />
                                        Sending
                                    </>
                                ) : (
                                    <>
                                        Submit Inquiry
                                        <ArrowUpRight
                                            size={18}
                                            strokeWidth={1.5}
                                        />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
